/**
 * Календарные даты по израильскому времени, независимо от часового пояса
 * компьютера, на котором открыта админка. Нужны там, где «дата» означает
 * «до конца этого дня в Израиле» — срок действия промокода.
 */
export const ISRAEL_TZ = 'Asia/Jerusalem';

const dateParts = new Intl.DateTimeFormat('en-US', {
  timeZone: ISRAEL_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

/** Израильские «настенные» часы в момент ms, разложенные как UTC-метка. */
function israelWallClockAsUtc(ms: number): number {
  const p: Record<string, number> = {};
  for (const part of dateParts.formatToParts(new Date(ms))) {
    if (part.type !== 'literal') p[part.type] = Number(part.value);
  }
  return Date.UTC(p.year!, p.month! - 1, p.day!, p.hour!, p.minute!, p.second!);
}

/** Дата YYYY-MM-DD в Израиле для момента iso. */
export function israelDateOf(iso: string): string {
  return new Date(israelWallClockAsUtc(new Date(iso).getTime())).toISOString().slice(0, 10);
}

/** Сегодняшняя дата в Израиле, YYYY-MM-DD. */
export function israelToday(): string {
  return israelDateOf(new Date().toISOString());
}

/** Календарная дата YYYY-MM-DD плюс n дней. */
export function addDays(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + n)).toISOString().slice(0, 10);
}

/**
 * Момент 23:59:59 даты YYYY-MM-DD по израильскому времени, в ISO (UTC).
 * Смещение берём у самой метки и пересчитываем второй раз — на случай
 * перехода на летнее или зимнее время в этот день.
 */
export function endOfIsraelDay(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const wall = Date.UTC(y!, m! - 1, d!, 23, 59, 59);
  let ts = wall - (israelWallClockAsUtc(wall) - wall);
  ts = wall - (israelWallClockAsUtc(ts) - ts);
  return new Date(ts).toISOString();
}

/** Момент iso как израильское время для <input type="datetime-local">: YYYY-MM-DDTHH:mm. */
export function israelLocalInputOf(iso: string): string {
  return new Date(israelWallClockAsUtc(new Date(iso).getTime())).toISOString().slice(0, 16);
}

/**
 * Израильское время YYYY-MM-DDTHH:mm (из <input type="datetime-local">) —
 * момент в ISO (UTC). Пересчёт дважды, как в endOfIsraelDay: в день перехода
 * на летнее или зимнее время смещение у метки другое.
 */
export function israelLocalToIso(local: string): string {
  const [date = '', time = ''] = local.split('T');
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  const wall = Date.UTC(y!, m! - 1, d!, hh!, mm!, 0);
  let ts = wall - (israelWallClockAsUtc(wall) - wall);
  ts = wall - (israelWallClockAsUtc(ts) - ts);
  return new Date(ts).toISOString();
}

/** dd.mm.yyyy в Израиле для момента iso. */
export function fmtIsraelDate(iso: string): string {
  const [y, m, d] = israelDateOf(iso).split('-');
  return `${d}.${m}.${y}`;
}
